import DeleteUserConfirmationDialog from 'components/DeleteUserDialog/DeleteUserConfirmationDialog/DeleteUserConfirmationDialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserService from 'services/user.service';
import { logOut } from 'utils/authentication';
import { notification } from 'utils/snackbar';

import { DeleteUserDialog } from './DeleteUserDialog';

const userService = new UserService();

type DeleteUserDialogProps = {
  deleteReasons?: string[];
  opened: boolean;
  close: () => void;
};

const DeleteUserDialogContainer = ({ deleteReasons, opened, close }: DeleteUserDialogProps): JSX.Element => {
  const { t } = useTranslation();

  const [reasons, setReasons] = useState<string[]>(deleteReasons);

  const [reason, setReason] = useState<string>(null);
  const [validation, setValidation] = useState({
    reason: true,
  });

  const [comment, setComment] = useState<string>('');

  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
  const [confirmationOpened, setConfirmationOpened] = useState<boolean>(false);
  const [confirmationErrors, setConfirmationErrors] = useState<string[]>([]);

  const resetValidation = () => {
    setValidation({
      reason: true,
    });
  };

  const reset = () => {
    resetValidation();
    setReason(null);
    setComment('');
    setConfirmationErrors([]);
  };

  const handleClose = () => {
    reset();
    close();
  };

  const deleteUser = async () => {
    try {
      setDeleteInProgress(true);

      const deleteUserData = {
        reason,
        comment,
      };

      await userService.deleteUser(deleteUserData, { silentFail: true });

      notification.showSuccessNotification(t('pages.settings.profile.delete.confirm.success'));
      handleClose();
      logOut();
    } catch (error) {
      if (error?.data?.data?.errors?.length > 0) {
        setConfirmationErrors(error?.data?.data?.errors);

        return;
      }

      notification.showErrorNotification({
        message: t('pages.settings.profile.delete.error.deletion-error'),
        error,
      });
      handleClose();
    } finally {
      setDeleteInProgress(false);
    }
  };

  const loadReasons = async () => {
    const loadedReasons = await userService.getDeleteReasons();

    if (loadedReasons) {
      setReasons(loadedReasons);
    }
  };

  const handleContinue = () => {
    resetValidation();

    if (!reason) {
      setValidation({ ...validation, reason: false });

      return;
    }

    setConfirmationOpened(true);
  };

  const closeConfirmation = () => {
    handleClose();
    setConfirmationOpened(false);
    setConfirmationErrors([]);
  };

  const handleSetReason = (radioReason) => {
    resetValidation();
    setReason(radioReason);
  };

  useEffect(() => {
    if (!deleteReasons || deleteReasons?.length === 0) {
      loadReasons();
    }
  }, []);

  const handleComment = (event) => setComment(event.target.value);
  const deleteUserDialogProps = {
    opened,
    confirmationOpened,
    handleClose,
    validation,
    reason,
    deleteReasons: reasons,
    handleSetReason,
    comment,
    handleComment,
    handleContinue,
  };

  return (
    <>
      <DeleteUserDialog {...deleteUserDialogProps} />
      <DeleteUserConfirmationDialog
        deleteInProgress={deleteInProgress}
        deleteUser={deleteUser}
        opened={confirmationOpened}
        close={closeConfirmation}
        errors={confirmationErrors}
      />
    </>
  );
};

export default DeleteUserDialogContainer;
